/**
 * @file pianoGiorno.js
 * Chiamate al BE reale (VITE_BE_BASE, default http://localhost:8080) per la griglia di pianificazione.
 * Ogni funzione tenta la chiamata e restituisce null in caso di errore (BE non disponibile).
 */

import { parseErrorMessage } from "../utils/apiError";
import { apiFetch } from './auth';

const BE_BASE = "/api";

/** Estrae il payload da SimpleResponse { status, info } oppure da un array nudo */
function extractInfo(data) {
  if (data === null || data === undefined) return null;
  if (Array.isArray(data)) return data;
  if (data.info) return data.info;
  return data;
}

/** Estrae lista di record da SimpleResponse paginata o da array diretto */
function extractList(data) {
  const info = extractInfo(data);
  if (!info) return null;
  if (Array.isArray(info)) return info;
  for (const key of [
    "content",
    "list",
    "rows",
    "data",
    "items",
    "records",
    "elements",
  ]) {
    if (Array.isArray(info[key])) return info[key];
  }
  if (typeof info === "object") {
    for (const val of Object.values(info)) {
      if (Array.isArray(val)) return val;
    }
  }
  return null;
}

/**
 * Normalizza un oggetto consulente proveniente dalla risposta /griglia:
 * - sede: pu� essere stringa o oggetto { nome, ... }
 * - bu: pu� essere stringa o oggetto BusinessUnit { descrizione, buCode, ... }
 * - tipo: derivato da flEsterno / flInterno se non presente come stringa
 */
function normalizeConsulente(c) {
  // sede: estrai la stringa dal possibile oggetto annidato
  let sede = c.sede ?? "";
  if (typeof sede === "object" && sede !== null) {
    sede = sede.nome ?? sede.descrizione ?? sede.nomeSede ?? "";
  }

  // bu: descrizione per la cella/etichetta; buCode: valore stabile usato per il filtro
  // (la descrizione resta solo l'etichetta mostrata nel dropdown checklist).
  let bu = c.bu ?? "";
  let buCode = "";
  if (typeof bu === "object" && bu !== null) {
    buCode = bu.buCode ?? "";
    bu = bu.descrizione ?? bu.buCode ?? "";
  } else {
    buCode = bu;
  }

  // tipo: usa il campo se gi� presente, altrimenti ricava dal flag
  let tipo = c.tipo ?? "";
  if (!tipo) {
    if (c.flEsterno !== undefined) tipo = c.flEsterno ? "Esterno" : "Interno";
    else if (c.flInterno !== undefined)
      tipo = c.flInterno ? "Interno" : "Esterno";
  }

  return { ...c, sede, bu, buCode, tipo };
}

/**
 * GET /PianificazioneGiorno/griglia?idHeader={id}&pm={pm}&compact={true|false}
 * Ritorna il formato { anno, mese, periodoAperto, nonLavorativi, consulenti }
 * gi� compatibile con ExcelGrid.
 * @param {number} idHeader
 * @param {{ compact?: boolean, pm?: string }} options
 */
export async function fetchGriglia(idHeader, { compact = false, pm } = {}) {
  try {
    const params = new URLSearchParams({ idHeader, compact: String(compact) });
    if (pm) params.append("pm", pm);
    const res = await apiFetch(
      `${BE_BASE}/PianificazioneGiorno/griglia?${params}`,
      { headers: { "Content-Type": "application/json" } },
    );
    if (!res.ok) return null;
    const data = await res.json();
    const griglia = extractInfo(data);
    if (!griglia) return null;
    // Normalizza i consulenti (sede come stringa, tipo da flEsterno/flInterno)
    if (Array.isArray(griglia.consulenti)) {
      griglia.consulenti = griglia.consulenti.map(normalizeConsulente);
    }
    return griglia;
  } catch {
    return null;
  }
}

/**
 * GET /cliente?page=0&size=2000
 * Ritorna array di { id, tag_cliente, nome_cliente, gruppo }
 */
export async function fetchClienti() {
  try {
    const res = await apiFetch(`${BE_BASE}/Cliente/get?page=0&size=2000`, {
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const items = extractList(data);
    if (!items) return null;
    return items.map((c) => ({
      id: c.id ?? c.idCliente ?? c.id_cliente,
      tag_cliente: c.tag_cliente ?? c.tagCliente ?? c.tag ?? c.codice ?? "",
      nome_cliente:
        c.nome_cliente ?? c.nomeCliente ?? c.nome ?? c.ragioneSociale ?? "",
      gruppo: c.gruppo ?? "",
    }));
  } catch {
    return null;
  }
}

/**
 * GET /Anagrafica/get?page=0&size=2000
 * Ritorna array di { id, consulente, area, businessUnit, sede, flEsterno, flAttivo, matricola }
 */
export async function fetchAnagrafica() {
  try {
    const res = await apiFetch(`${BE_BASE}/Anagrafica/get?page=0&size=2000`, {
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return extractList(data);
  } catch {
    return null;
  }
}

/**
 * POST /Header/post � aggiorna dataFinePianificazione su un singolo header.
 * Usato da DateLimitePage per applicare la deadline calcolata.
 *
 * Inviamo la data come timestamp numerico (ms epoch, mezzogiorno UTC) anzich�
 * stringa "YYYY-MM-DD" per evitare il bug thread-safety di SimpleDateFormat nel
 * CrudCompanion BE: con un Number il BE esegue semplicemente `new Date(long)`
 * senza parsing, e l'ora 12:00 UTC garantisce il giorno corretto anche variando
 * il timezone del server.
 */
export async function setHeaderDeadline(idHeader, dataFinePianificazione, time = "10:00") {
  // Inviamo l'ora locale specificata: se la colonna BE � DATETIME/TIMESTAMP verr� salvata
  // l'ora corretta; se � DATE il tempo viene truncato a mezzanotte dal driver SQL
  // ma la data � comunque corretta (un orario ragionevole non attraversa mai il boundary di giorno
  // per qualsiasi timezone ragionevole per questo progetto).
  const ts = new Date(`${dataFinePianificazione}T${time}:00`).getTime();
  const res = await apiFetch(`${BE_BASE}/Header/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idHeader, dataFinePianificazione: ts }),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "setHeaderDeadline"));
  }
  return res.json().catch(() => null);
}

/**
 * DELETE /PianificazioneGiorno/removeConsulente?idHeader=X&consulente=Y&pgId=Z
 * Rimuove un consulente (slot) da una testata.
 * Se pgId � null/undefined rimuove tutte le righe del consulente.
 */
export async function removeConsulente({ idHeader, consulente, pgId }) {
  const params = new URLSearchParams({ idHeader, consulente });
  if (pgId != null) params.append("pgId", pgId);

  const res = await apiFetch(
    `${BE_BASE}/PianificazioneGiorno/removeConsulente?${params}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "removeConsulente"));
  }
  const data = await res.json().catch(() => null);
  return data ? extractInfo(data) : null;
}

/**
 * POST /Anagrafica/post � crea o aggiorna un record anagrafica.
 * DELETE /Anagrafica/delete?id=X � elimina per id.
 */
export async function saveAnagrafica(body) {
  const res = await apiFetch(`${BE_BASE}/Anagrafica/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "saveAnagrafica"));
  }
  return res.json().catch(() => null);
}

export async function deleteAnagraficaRecord(id) {
  const res = await apiFetch(
    `${BE_BASE}/Anagrafica/delete?id=${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "deleteAnagrafica"));
  }
}

/**
 * POST /Cliente/post � crea o aggiorna un cliente.
 * DELETE /Cliente/delete?pk=X � elimina per codice (il BE usa il param 'pk' come tutti i delete).
 */
export async function saveCliente(body) {
  const res = await apiFetch(`${BE_BASE}/Cliente/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "saveCliente"));
  }
  return res.json().catch(() => null);
}

export async function deleteClienteRecord(codice) {
  const res = await apiFetch(
    `${BE_BASE}/Cliente/delete?pk=${encodeURIComponent(codice)}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "deleteCliente"));
  }
}

/**
 * POST /PianificazioneGiorno/post?acceptNull=true
 * Aggiorna un record esistente (id obbligatorio) con i dati del giorno.
 */

export async function savePianificazioneGiorno({
  id,
  cliente,
  ore,
  activity_type,
  activity_notes,
}) {
  /*
   * acceptNull=false: CrudCompanion ignora i campi null nel body.
   * Questo evita la corruzione dei FK (consulente, header, slotIndex)
   * che non sono inclusi nel body ma Java li deserializza come null.
   *
   * Il colore usa sempre un valore non-null
   * in modo che con acceptNull=false venga sempre aggiornato.
   */
  const body = {
    id,
    cliente: cliente || null,
    ore: ore != null && ore !== "" ? Number(ore) : null,
    tipoAttivita: activity_type ?? "Standard",
    note: activity_notes ?? "",
  };
  const res = await apiFetch(
    `${BE_BASE}/PianificazioneGiorno/post?acceptNull=false`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "savePianificazioneGiorno"));
  }
  return res.json().catch(() => null);
}

/**
 * POST /PianificazioneGiorno/post?acceptNull=true
 * Crea un nuovo record per un giorno festivo (nessun placeholder esistente).
 * Richiede includiFestivi=true per bypassare il controllo BE.
 */
export async function insertPianificazioneGiornoFestivo({
  idHeader,
  consulente,
  giorno,
  pm,
  cliente,
  ore,
  activity_type,
  activity_notes,
}) {
  const body = {
    header: idHeader,
    consulente,
    giorno,
    includiFestivi: true,
    cliente: cliente || null,
    ore: ore != null && ore !== "" ? Number(ore) : null,
    tipoAttivita: activity_type ?? "Standard",
    note: activity_notes ?? "",
    pm: pm || null,
  };
  const res = await apiFetch(
    `${BE_BASE}/PianificazioneGiorno/post?acceptNull=true`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "insertPianificazioneGiornoFestivo"));
  }
  return res.json().catch(() => null);
}

/**
 * POST /PianificazioneGiorno/bulk
 * Aggiorna pi� record in una sola chiamata.
 * items: [{ id, cliente, ore, colore, note }, ...]
 */
export async function bulkPianificazioneGiorno({
  ids,
  cliente,
  ore,
  activity_type,
  activity_notes,
}) {
  const body = {
    ids,
    cliente: cliente || null,
    ore: ore != null && ore !== "" ? Number(ore) : null,
    tipoAttivita: activity_type ?? null,
    note: activity_notes ?? "",
  };
  const res = await apiFetch(`${BE_BASE}/PianificazioneGiorno/bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "bulkPianificazioneGiorno"));
  }
  return res.json().catch(() => null);
}

/**
 * POST /PianificazioneGiorno/grigliaBulk
 * Carica pi� griglie in una sola chiamata HTTP.
 * @param {number[]} headerIds
 * @param {{ pm?: string }} options
 * @returns {Array|null} array di griglie nello stesso ordine degli headerIds
 */
export async function fetchGrigliaBulk(headerIds, { pm } = {}) {
  try {
    const body = { headerIds };
    if (pm) body.pm = pm;
    const res = await apiFetch(`${BE_BASE}/PianificazioneGiorno/grigliaBulk`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const list = extractInfo(data);
    if (!Array.isArray(list)) return null;
    return list.map((g) => {
      if (g && Array.isArray(g.consulenti)) {
        g.consulenti = g.consulenti.map(normalizeConsulente);
      }
      return g;
    });
  } catch {
    return null;
  }
}

/**
 * GET /Anagrafica/consulentiPrecedenti
 * Restituisce le anagrafiche dei consulenti usati dal PM nelle N pianificazioni precedenti.
 */
export async function getConsulentiPrecedenti(
  pm,
  mese,
  anno,
  numeroPianificazioni,
) {
  const params = new URLSearchParams({
    pm,
    mese: String(mese),
    anno: String(anno),
    numeroPianificazioni: String(numeroPianificazioni),
  });
  const res = await apiFetch(
    `${BE_BASE}/Anagrafica/consulentiPrecedenti?${params}`,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  if (!res.ok) throw new Error(await parseErrorMessage(res, "getConsulentiPrecedenti"));
  const data = await res.json();
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.info)) return data.info;
  return extractList(data) ?? [];
}

/**
 * GET /Header/get?page=0&size=2000&sort=idHeader,desc
 * Ritorna array grezzo di header (normalizzazione delegata al consumer).
 */
export async function fetchHeaders() {
  const res = await apiFetch(
    `${BE_BASE}/Header/get?page=0&size=2000&sort=idHeader,desc`,
    { headers: { "Content-Type": "application/json" } },
  );
  if (!res.ok) throw new Error(await parseErrorMessage(res, "fetchHeaders"));
  const data = await res.json();
  return extractList(data) ?? [];
}

/**
 * POST /Header/post � crea o aggiorna un header di pianificazione.
 * body: { pm, note, stato, mese, anno, idHeader? }
 */
export async function saveHeader(body) {
  const res = await apiFetch(`${BE_BASE}/Header/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "saveHeader"));
  }
  return res.json().catch(() => null);
}

/**
 * DELETE /Header/delete?pk={id}
 */
export async function deleteHeader(id) {
  const res = await apiFetch(
    `${BE_BASE}/Header/delete?pk=${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "deleteHeader"));
  }
}

/**
 * POST /PianificazioneGiorno/addConsulente
 * Crea un record per ogni giorno lavorativo del mese con ore=null.
 * payload: { idHeader, consulente: "NomeCognome" | { id: N }, pm?, colore?, note? }
 */
export async function addConsulente(payload) {
  const res = await apiFetch(`${BE_BASE}/PianificazioneGiorno/addConsulente`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "addConsulente"));
  }
  const data = await res.json();
  return extractInfo(data);
}

/**
 * POST /PianificazioneGiorno/addConsulenti
 * Aggiunge pi� consulenti in una sola chiamata (bulk di addConsulente).
 * payload: { idHeader, consulenti: string[], pm?, colore?, note? }
 * Ritorna { aggiunti: object[], errori: string[] } � gli errori sono per singolo
 * consulente, non come eccezione: la chiamata HTTP va sempre a buon fine se raggiunge il BE.
 */
export async function addConsulenti(payload) {
  const res = await apiFetch(`${BE_BASE}/PianificazioneGiorno/addConsulenti`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "addConsulenti"));
  }
  const data = await res.json();
  return extractInfo(data);
}

// --- Note consulente ----------------------------------------------------------

/**
 * GET /Nota/get?search=header.idHeader:X&page=0&size=2000&sort=id,asc
 * Ritorna le note per un header, ordinate per ID (l'ordine corrisponde agli slot).
 * @returns {Array<{id, consulente, note, pm}>}
 */
export async function fetchNote(idHeader) {
  try {
    const params = new URLSearchParams({
      search: `|header.idHeader=${idHeader}`,
      page: "0",
      size: "2000",
      sort: "id,asc",
    });
    const res = await apiFetch(`${BE_BASE}/Nota/get?${params}`, {
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return [];
    const data = await res.json();
    const list = extractList(data);
    if (!list) return [];
    return list.map((n) => ({
      id: n.id,
      consulente: n.consulente,
      note: n.note ?? "",
      pm: n.pm ?? null,
      idPianificazioneGiorno: n.idPianificazioneGiorno ?? null,
    }));
  } catch {
    return [];
  }
}

/**
 * POST /Nota/post � crea o aggiorna una nota consulente.
 * Se id � fornito aggiorna, altrimenti crea.
 */
export async function saveNota({ id, idHeader, consulente, note, pm }) {
  const body = {
    header: idHeader,
    consulente,
    note: note ?? "",
    pm: pm || null,
  };
  if (id != null) body.id = id;
  const res = await apiFetch(`${BE_BASE}/Nota/post?acceptNull=false`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseErrorMessage(res, "saveNota"));
  }
  return res.json().catch(() => null);
}

// --- BusinessUnit -------------------------------------------------------------

export async function fetchBusinessUnits() {
  try {
    const res = await apiFetch(
      `${BE_BASE}/BusinessUnit/get?page=0&size=2000&sort=id,asc`,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return extractList(data);
  } catch {
    return null;
  }
}

export async function saveBusinessUnit(body) {
  const res = await apiFetch(`${BE_BASE}/BusinessUnit/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "saveBusinessUnit"));
  return extractInfo(await res.json());
}

export async function deleteBusinessUnit(pk) {
  const res = await apiFetch(`${BE_BASE}/BusinessUnit/delete?pk=${pk}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "deleteBusinessUnit"));
}

export async function softDeleteBusinessUnit(pk) {
  const res = await apiFetch(`${BE_BASE}/BusinessUnit/post?acceptNull=false`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: pk, flAttivo: false }),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "softDeleteBusinessUnit"));
}

// --- Sede ----------------------------------------------------------------------

export async function fetchSedi() {
  try {
    const res = await apiFetch(
      `${BE_BASE}/Sede/get?page=0&size=2000&sort=idSede,asc`,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return extractList(data);
  } catch {
    return null;
  }
}

export async function saveSede(body) {
  const res = await apiFetch(`${BE_BASE}/Sede/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "saveSede"));
  return extractInfo(await res.json());
}

export async function deleteSede(pk) {
  const res = await apiFetch(
    `${BE_BASE}/Sede/delete?pk=${encodeURIComponent(pk)}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(await parseErrorMessage(res, "deleteSede"));
}

export async function softDeleteSede(pk) {
  const res = await apiFetch(`${BE_BASE}/Sede/post?acceptNull=false`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idSede: pk, flAttivo: false }),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "softDeleteSede"));
}

// --- Utente (Project Managers) ------------------------------------------------

export async function fetchUtenti() {
  try {
    const res = await apiFetch(
      `${BE_BASE}/Utente/get?page=0&size=2000&sort=id,asc`,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return extractList(data);
  } catch {
    return null;
  }
}

export async function saveUtente(body) {
  const res = await apiFetch(`${BE_BASE}/Utente/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "saveUtente"));
  return extractInfo(await res.json());
}

export async function deleteUtente(pk) {
  const res = await apiFetch(`${BE_BASE}/Utente/delete?pk=${pk}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "deleteUtente"));
}

// --- Area ---------------------------------------------------------------------

export async function fetchAree() {
  try {
    const res = await apiFetch(
      `${BE_BASE}/Area/get?page=0&size=2000&sort=id,asc`,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return extractList(data);
  } catch {
    return null;
  }
}

export async function saveArea(body) {
  const res = await apiFetch(`${BE_BASE}/Area/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "saveArea"));
  return extractInfo(await res.json());
}

export async function deleteArea(pk) {
  const res = await apiFetch(`${BE_BASE}/Area/delete?pk=${pk}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "deleteArea"));
}

export async function softDeleteArea(pk) {
  const res = await apiFetch(`${BE_BASE}/Area/post?acceptNull=false`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: pk, flAttivo: false }),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "softDeleteArea"));
}

// --- GiornoNonLavorativo ------------------------------------------------------

export async function fetchNonWorkingDays() {
  try {
    const res = await apiFetch(
      `${BE_BASE}/GiornoNonLavorativo/get?page=0&size=2000&sort=data,asc`,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return extractList(data);
  } catch {
    return null;
  }
}

export async function saveNonWorkingDay(body) {
  const res = await apiFetch(`${BE_BASE}/GiornoNonLavorativo/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "saveNonWorkingDay"));
  return extractInfo(await res.json());
}

export async function deleteNonWorkingDay(pk) {
  const res = await apiFetch(`${BE_BASE}/GiornoNonLavorativo/delete?pk=${pk}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "deleteNonWorkingDay"));
}

// --- DataLimitePianificazione --------------------------------------------------

export async function fetchDeadlines() {
  try {
    const res = await apiFetch(
      `${BE_BASE}/StatoPianificazione/get?page=0&size=2000&sort=anno,asc`,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return extractList(data);
  } catch {
    return null;
  }
}

export async function saveDeadline(body) {
  const res = await apiFetch(`${BE_BASE}/StatoPianificazione/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "saveDeadline"));
  return extractInfo(await res.json());
}

export async function deleteDeadline(pk) {
  const res = await apiFetch(
    `${BE_BASE}/StatoPianificazione/delete?pk=${pk}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(await parseErrorMessage(res, "deleteDeadline"));
}

/**
 * POST /StatoPianificazione/eseguiJob — esegue subito un giro del job della macchina a stati
 * (righe dovute di TB_PIANIFICAZIONE_STATO), senza attendere il cron su alfalucrezia-scheduler.
 */
export async function eseguiJob() {
  const res = await apiFetch(`${BE_BASE}/StatoPianificazione/eseguiJob`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "eseguiJob"));
  return res.json().catch(() => null);
}

// --- LinkSeniorPm (associazione Senior "Richiedente" -> PM) ------------------
export async function fetchLinkSeniorPm() {
  try {
    const res = await apiFetch(
      `${BE_BASE}/LinkSeniorPm/get?page=0&size=2000`,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return extractList(data);
  } catch {
    return null;
  }
}

export async function saveLinkSeniorPm(body) {
  const res = await apiFetch(`${BE_BASE}/LinkSeniorPm/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "saveLinkSeniorPm"));
  return extractInfo(await res.json());
}

export async function deleteLinkSeniorPm(pk) {
  const res = await apiFetch(`${BE_BASE}/LinkSeniorPm/delete?pk=${pk}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res, "deleteLinkSeniorPm"));
}

/**
 * GET /Kpi/sintesiPerPm � KPI mensili aggregati per PM.
 * Restituisce array di { pm, idHeader, numRisorse, oreMensili, orePianificate, oreTeoriche }.
 * @param {string} mese  es. "05"
 * @param {string} anno  es. "2026"
 * @param {string} [pm]  filtro opzionale sul nome/email del PM
 */
export async function fetchSintesiPerPm(mese, anno, pm) {
  const params = new URLSearchParams({ mese, anno });
  if (pm) params.append('pm', pm);
  const res = await apiFetch(`${BE_BASE}/Kpi/sintesiPerPm?${params}`);
  if (!res.ok) throw new Error(`fetchSintesiPerPm failed: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchOreDisponibili(mese, anno) {
  const params = new URLSearchParams({ mese, anno });
  const res = await apiFetch(`${BE_BASE}/Kpi/oreDisponibili?${params}`);
  if (!res.ok) throw new Error(`fetchOreDisponibili failed: ${res.status}`);
  return res.json();
}

export async function fetchConsulentiPerPm(mese, anno) {
  const params = new URLSearchParams({ mese, anno });
  const res = await apiFetch(`${BE_BASE}/Kpi/consulentiPerPm?${params}`);
  if (!res.ok) throw new Error(`fetchConsulentiPerPm failed: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

// --- Projects -----------------------------------------------------------------

export async function fetchProjects() {
  try {
    const res = await apiFetch(`${BE_BASE}/Project/get?page=0&size=2000&sort=projectId,asc`, { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) return [];
    return extractList(await res.json()) ?? [];
  } catch { return []; }
}
export async function saveProject(body) {
  const res = await apiFetch(`${BE_BASE}/Project/post`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`saveProject failed: ${res.status}`);
  return extractInfo(await res.json());
}
export async function deleteProject(pk) {
  const res = await apiFetch(`${BE_BASE}/Project/delete?pk=${pk}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`deleteProject failed: ${res.status}`);
}

// --- Tasks --------------------------------------------------------------------

export async function fetchTasks() {
  try {
    const res = await apiFetch(`${BE_BASE}/Task/get?page=0&size=2000&sort=taskId,asc`, { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) return [];
    return extractList(await res.json()) ?? [];
  } catch { return []; }
}
export async function saveTask(body) {
  const res = await apiFetch(`${BE_BASE}/Task/post`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`saveTask failed: ${res.status}`);
  return extractInfo(await res.json());
}
export async function deleteTask(pk) {
  const res = await apiFetch(`${BE_BASE}/Task/delete?pk=${pk}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`deleteTask failed: ${res.status}`);
}

// --- TaskResourceLinks --------------------------------------------------------

export async function fetchTaskResourceLinks() {
  try {
    const res = await apiFetch(`${BE_BASE}/TaskResourceLink/get?page=0&size=2000&sort=taskResourceLinkId,asc`, { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) return [];
    return extractList(await res.json()) ?? [];
  } catch { return []; }
}
export async function saveTaskResourceLink(body) {
  const res = await apiFetch(`${BE_BASE}/TaskResourceLink/post`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`saveTaskResourceLink failed: ${res.status}`);
  return extractInfo(await res.json());
}
export async function deleteTaskResourceLink(pk) {
  const res = await apiFetch(`${BE_BASE}/TaskResourceLink/delete?pk=${pk}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`deleteTaskResourceLink failed: ${res.status}`);
}

// --- TaskResourcePatterns -----------------------------------------------------

export async function fetchTaskResourcePatterns() {
  try {
    const res = await apiFetch(`${BE_BASE}/TaskResourcePattern/get?page=0&size=2000&sort=id,asc`, { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) return [];
    return extractList(await res.json()) ?? [];
  } catch { return []; }
}
export async function saveTaskResourcePattern(body) {
  const res = await apiFetch(`${BE_BASE}/TaskResourcePattern/post`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`saveTaskResourcePattern failed: ${res.status}`);
  return extractInfo(await res.json());
}
export async function deleteTaskResourcePattern(pk) {
  const res = await apiFetch(`${BE_BASE}/TaskResourcePattern/delete?pk=${pk}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`deleteTaskResourcePattern failed: ${res.status}`);
}
